import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { RichText } from "@/components/site/RichText";
import { Faq, faqLd } from "@/components/site/Faq";
import { useSeo, SITE_URL, absUrl, ORGANIZATION } from "@/lib/seo";
import { EN_PAGES } from "@/lib/enPages";

const SKIN_HUB = "/en/product/serum-anti-taches-lumina-vg";
const NAME = { ...Object.fromEntries(Object.values(EN_PAGES).map((p) => [p.path, p.name])), [SKIN_HUB]: "Skin care · LUMINA V.G." };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");
const CRUMBS = [{ to: "/en", label: "Ozentya (English)" }, { to: "/en/blog", label: "Hair care blog" }];
const NOTICE = "Cosmetic hair-care advice: it does not replace the opinion of a doctor or dermatologist. Extrême Repouss is not suitable during pregnancy or breastfeeding.";
const SKIN_NOTICE = "Cosmetic skincare advice: it does not replace the opinion of a doctor or dermatologist. Results vary from person to person, and daily sunscreen is essential when you care for dark spots.";

function useEnArticles() {
  return useQuery({
    queryKey: ["articles", "en"],
    queryFn: async () => { const r = await api.get("/articles", { params: { lang: "en" } }); return Array.isArray(r.data) ? r.data : []; },
    staleTime: 60_000,
  });
}

function Cards({ articles, testid }) {
  if (!articles?.length) return null;
  return (
    <div data-testid={testid} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((a) => (
        <Link key={a.slug} to={`/en/blog/${a.slug}`} data-testid={`en-article-card-${a.slug}`} className="group block rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6 transition-colors duration-300 hover:border-[var(--gold)]">
          <p className="eyebrow mb-3">{NAME[a.hub] || "Hair care"}</p>
          <h3 className="font-display text-2xl leading-snug group-hover:text-[var(--gold-soft)]">{a.title}</h3>
          <p className="mt-3 line-clamp-3 text-sm text-[var(--parchment)]/80">{a.excerpt}</p>
          <span className="mt-4 flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]">Read the article <ArrowUpRight className="h-3.5 w-3.5" /></span>
        </Link>
      ))}
    </div>
  );
}

/** Every English guide, so search engines and readers find them all from one place. */
export function EnGuidesNav() {
  return (
    <nav aria-label="English guides" data-testid="en-guides-nav" className="mt-16 flex flex-wrap gap-2">
      {Object.values(EN_PAGES).filter((p) => p.path !== "/en").map((p) => (
        <Link key={p.path} to={p.path} data-testid={`en-guide-link${p.path.replace(/\//g, "-")}`} className="flex h-10 items-center rounded-full border border-[var(--line)] px-5 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">{p.name}</Link>
      ))}
    </nav>
  );
}

export function EnBlogIndex() {
  const { data = [], isLoading } = useEnArticles();
  useSeo({
    title: "Afro hair care blog: hair loss, edges, locs, beard | Ozentya", path: "/en/blog", lang: "en",
    description: "Practical, honest articles for Black women and men in the US, UK and Canada: thinning edges, traction alopecia, 4C breakage, locs and beard care.",
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "Blog", name: "Ozentya hair care blog", url: `${SITE_URL}/en/blog`, inLanguage: "en", publisher: ORGANIZATION }, breadcrumbLd(SITE_URL, CRUMBS)] },
  });
  return (
    <main lang="en" data-testid="en-blog-page" className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={CRUMBS} />
      <p className="eyebrow mb-6">The Ozentya journal</p>
      <h1 className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Afro hair care blog</h1>
      <p className="mt-8 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">Clear answers to the questions Black women and men ask about thinning edges, traction alopecia, breakage, locs and beards. New articles every week.</p>
      <EnGuidesNav />
      <section className="mt-16">
        <h2 className="sr-only">All articles</h2>
        {isLoading ? <p className="text-[var(--muted)]">Loading…</p> : data.length ? <Cards articles={data} testid="en-blog-list" /> : <p data-testid="en-blog-empty" className="text-[var(--parchment)]/80">The first articles are on their way. Meanwhile, browse our guides above.</p>}
      </section>
    </main>
  );
}

export function EnArticlePage() {
  const { slug } = useParams();
  const { data: a, isLoading, isError } = useQuery({ queryKey: ["article", slug], queryFn: async () => (await api.get(`/articles/${slug}`)).data, retry: false });
  const related = (useEnArticles().data || []).filter((x) => x.slug !== slug && x.hub === a?.hub).slice(0, 3);
  const path = `/en/blog/${slug}`;
  const crumbs = [...CRUMBS, { to: path, label: a?.title || "" }];
  const ok = a && a.lang === "en";
  useSeo(ok ? {
    title: (a.seo_title || a.title).length > 50 ? a.seo_title || a.title : `${a.seo_title || a.title} | Ozentya`, description: a.description, path, image: a.image, type: "article", lang: "en",
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "Article", headline: a.title, description: a.description, image: [absUrl(a.image)].filter(Boolean), datePublished: a.published_at, dateModified: a.updated_at || a.published_at,
        author: { "@type": "Organization", name: "The Ozentya team", url: `${SITE_URL}/en` }, publisher: ORGANIZATION, mainEntityOfPage: `${SITE_URL}${path}`, inLanguage: "en" },
      breadcrumbLd(SITE_URL, crumbs), ...(a.faq?.length ? [faqLd(a.faq)] : []),
    ] },
  } : { title: isError ? "Article not found | Ozentya" : undefined, noindex: true, lang: "en" });

  if (isLoading) return <main className="min-h-screen pt-44 text-center text-[var(--muted)]">Loading…</main>;
  if (isError || !ok) return (
    <main lang="en" data-testid="en-article-not-found" className="flex min-h-screen flex-col items-center justify-center px-5 pt-36 text-center">
      <h1 className="font-display text-4xl italic">Article not found</h1>
      <Link to="/en/blog" className="mt-6 text-[var(--gold-soft)] underline">See all articles</Link>
    </main>
  );
  return (
    <main lang="en" data-testid="en-article-page" className="px-5 pb-32 pt-44 md:px-10">
      <article className="mx-auto max-w-3xl">
        <Breadcrumb items={crumbs} />
        {NAME[a.hub] ? <Link to={a.hub} data-testid="en-article-hub-link" className="eyebrow mb-6 inline-block hover:underline">{NAME[a.hub]}</Link> : null}
        <h1 data-testid="en-article-title" className="font-display text-4xl leading-[1.08] sm:text-5xl">{a.title}</h1>
        <p className="mt-5 text-xs uppercase tracking-[0.2em] text-[var(--parchment)]/70">By the Ozentya team · <time dateTime={a.updated_at || a.published_at}>Updated {fmt(a.updated_at || a.published_at)}</time></p>
        {a.image ? <img src={a.image} alt={a.title} width="1024" height="1024" className="mt-10 aspect-[16/9] w-full rounded-sm object-cover" /> : null}
        <div className="mt-10"><RichText source={a.body} /></div>
        <p data-testid="en-article-notice" className="mt-10 flex gap-3 rounded-sm border border-[var(--gold)]/40 bg-[rgba(217,180,106,0.08)] p-5 text-sm leading-relaxed text-[var(--parchment)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" aria-hidden="true" />{a.hub === SKIN_HUB ? SKIN_NOTICE : NOTICE}
        </p>
        <Faq items={a.faq} title="Frequently asked questions" />
        <Link to="/en/shop" data-testid="en-article-shop-link" className="mt-12 inline-flex h-12 items-center gap-2 rounded-full bg-[var(--gold)] px-7 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-transform hover:-translate-y-0.5">Shop Ozentya · worldwide shipping <ArrowUpRight className="h-4 w-4" /></Link>
      </article>
      <section className="mx-auto mt-24 max-w-[1440px]">
        {related.length ? <><h2 className="mb-8 font-display text-3xl">On the same topic</h2><Cards articles={related} testid="en-related-articles" /></> : null}
        <EnGuidesNav />
      </section>
    </main>
  );
}
